import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { advertiserApi } from '../../services/advertiserApi';

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function statusColor(status: string): 'default' | 'success' | 'warning' | 'info' {
  if (status === 'active') return 'success';
  if (status === 'pending') return 'warning';
  if (status === 'suspended') return 'info';
  return 'default';
}

/**
 * Advertiser overview page — summary cards (campaigns by status,
 * placements, spend) and a "Create campaign" CTA. Impression / click
 * counts are zero at MVP because the event tables aren't wired yet
 * (Phase 4 follow-up).
 */
export function AdvertiserDashboardPage() {
  const dashboardQuery = useQuery({
    queryKey: ['advertiser', 'dashboard'],
    queryFn: () => advertiserApi.getDashboard(),
  });

  if (dashboardQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading dashboard" />
      </Box>
    );
  }
  if (dashboardQuery.isError) {
    return (
      <Alert severity="error" role="alert">
        Failed to load your dashboard: {(dashboardQuery.error as Error).message}
      </Alert>
    );
  }

  const { account, summary } = dashboardQuery.data!;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem' }}>
            {account.companyName}
          </Typography>
          <Chip
            size="small"
            label={`Account status: ${account.status}`}
            color={statusColor(account.status)}
            sx={{ mt: 1 }}
          />
        </Box>
        <Button
          component={RouterLink}
          to="/dashboard/advertiser/campaigns?new=1"
          variant="contained"
          size="large"
        >
          Create campaign
        </Button>
      </Stack>

      {account.status === 'pending' ? (
        <Alert severity="info" sx={{ mb: 3 }} role="status">
          Your advertiser account is pending review. You can build campaigns and placements now;
          they go live once an admin activates the account.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        <SummaryCard label="Total campaigns" value={summary.campaigns.total} />
        <SummaryCard label="Active" value={summary.campaigns.active} />
        <SummaryCard label="Paused" value={summary.campaigns.paused} />
        <SummaryCard label="Drafts" value={summary.campaigns.draft} />
        <SummaryCard label="Placements" value={summary.placementCount} />
        <SummaryCard label="Total spend" value={dollars(summary.totalSpendCents)} />
        <SummaryCard label="Impressions" value={summary.impressions} note="MVP — see roadmap" />
        <SummaryCard label="Clicks" value={summary.clicks} note="MVP — see roadmap" />
      </Box>
    </Box>
  );
}

interface SummaryCardProps {
  label: string;
  value: number | string;
  note?: string;
}

function SummaryCard({ label, value, note }: SummaryCardProps) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h2" component="p" sx={{ fontSize: '2rem', mt: 1 }}>
          {value}
        </Typography>
        {note ? (
          <Typography variant="caption" color="text.secondary">
            {note}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default AdvertiserDashboardPage;
